// Engaged time: how long the visitor was actually ATTENDING to the page, as
// opposed to how long the page was open.
//
// Why this exists. `$prev_pageview_duration` is wall-clock, and the A/B report
// showed what that costs: over a 14-day A/A window where both arms served the
// identical page, mean site time came out 249.6s vs 1927.3s — one visitor left a
// tab open for 3.3 days and supplied 84% of the difference. The A/B dashboard
// works around that with a median and a 60s threshold; this measures the thing we
// actually wanted instead.
//
// The subtlety worth knowing: `emitPageLeave` already stops the wall clock at the
// first `visibilitychange`, so tab-switching is NOT the leak. `visibilityState`
// stays `'visible'` when the browser window is merely behind ANOTHER APPLICATION,
// or when the visitor walks away from a focused window. That is the dominant
// parked-tab case, and only `document.hasFocus()` plus an idle cap catches it.
//
// Model: no timers. We accumulate lazily by closing the interval since the last
// sample whenever something happens (activity, focus, visibility, or a read).
// Each interval is credited only if the page was focused and visible, and is
// truncated at `lastActivityAt + IDLE_GRACE_MS` so an activity-free stretch earns
// the grace period once rather than the hours until the visitor returns.

// Reading a paragraph without touching anything is normal, so the grace is
// generous. Walking away is bounded to a single minute of credit.
const IDLE_GRACE_MS = 60_000
// Pointer moves fire ~60/s; closing the interval that often is pointless. Any
// gap long enough for the idle cap to matter is far longer than this.
const SAMPLE_THROTTLE_MS = 500

const ACTIVITY_EVENTS = ['scroll', 'pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel'] as const

let engagedMs = 0
let lastSampleAt = 0
let lastActivityAt = 0
// Focused AND visible. Idle is deliberately NOT part of this flag — the interval
// cap in `accrue` handles idleness, which keeps the state to one boolean.
let focused = false

const canAttend = (): boolean => {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
    return false
  }
  // Fall back to visibility alone where `hasFocus` is missing. Over-counting a
  // little in an odd embedded browser beats reporting zero engaged time for
  // every visitor using it — a silent, uniform zero is the failure mode that
  // would be hardest to notice once this metric is promoted.
  return typeof document.hasFocus === 'function' ? document.hasFocus() : true
}

// Close the interval [lastSampleAt, now] and bank whatever part of it counts.
const accrue = (now: number) => {
  if (focused && lastSampleAt > 0) {
    const until = Math.min(now, lastActivityAt + IDLE_GRACE_MS)
    if (until > lastSampleAt) {
      engagedMs += until - lastSampleAt
    }
  }
  lastSampleAt = now
}

const setFocused = (next: boolean, now: number) => {
  if (next === focused) {
    return
  }
  if (focused) {
    // Bank BEFORE dropping the flag, or the interval leading up to a blur is
    // lost — `accrue` would see focused=false and credit nothing.
    accrue(now)
  }
  focused = next
  lastSampleAt = now
}

const onActivity = () => {
  const now = Date.now()
  if (now - lastSampleAt >= SAMPLE_THROTTLE_MS) {
    // Must run BEFORE lastActivityAt moves: the gap being closed is judged
    // against the PREVIOUS activity. Otherwise a visitor who returns after an
    // hour retroactively converts that idle hour into engaged time.
    accrue(now)
  }
  lastActivityAt = now
  setFocused(canAttend(), now)
}

const onFocusChange = () => {
  const now = Date.now()
  const next = canAttend()
  setFocused(next, now)
  if (next) {
    // Coming back to the page is itself activity — otherwise the idle cap would
    // still be measured from whenever the visitor last moved before leaving.
    lastActivityAt = now
  }
}

/** Restart accounting for a new pageview. Call when `$pageview` is emitted. */
export const resetEngagedTime = () => {
  const now = Date.now()
  engagedMs = 0
  lastSampleAt = now
  lastActivityAt = now
  focused = canAttend()
}

/** Engaged seconds on the current pageview. Call when `$pageleave` is emitted. */
export const readEngagedSeconds = (): number => {
  accrue(Date.now())
  return Math.round(engagedMs / 100) / 10
}

/** Attach listeners. Returns a teardown for the engine's effect cleanup.
 *  The optional callback reports trusted, intentional input. The engine passes
 *  one on every page — whether it means anything is decided per pageview by the
 *  callback itself, since only the pageview knows if it was newsletter-
 *  attributed. The added per-event work is a boolean read and a string compare,
 *  next to the `Date.now()` + `document.hasFocus()` `onActivity` already does. */
export const startEngagedTime = (onTrustedActivity?: () => void): (() => void) => {
  if (typeof document === 'undefined') {
    return () => {}
  }
  resetEngagedTime()
  const activityHandler = onTrustedActivity
    ? (event: Event) => {
        onActivity()
        // Script-dispatched events are not human proof, and pointermove can be
        // emitted merely because a cursor was already over the page. The other
        // activity types require an intentional scroll, press, touch, or wheel.
        if (event.isTrusted && event.type !== 'pointermove') {
          onTrustedActivity()
        }
      }
    : onActivity
  for (const name of ACTIVITY_EVENTS) {
    document.addEventListener(name, activityHandler, { passive: true, capture: true })
  }
  document.addEventListener('visibilitychange', onFocusChange)
  window.addEventListener('focus', onFocusChange)
  window.addEventListener('blur', onFocusChange)
  return () => {
    for (const name of ACTIVITY_EVENTS) {
      document.removeEventListener(name, activityHandler, { capture: true })
    }
    document.removeEventListener('visibilitychange', onFocusChange)
    window.removeEventListener('focus', onFocusChange)
    window.removeEventListener('blur', onFocusChange)
  }
}
